import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Alert, CircularProgress, TextField, Chip, Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import { formatDate } from "../helpers";
import type { SubscriptionData, AddressGroup, AvailableDate } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  addressId: string | null;
  addressGroups: AddressGroup[];
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
  onRefresh: () => void;
}

export function ChangeDeliveryModal({ open, onClose, sub, addressId, addressGroups, onSuccess, onError, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [newDate, setNewDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableDates, setAvailableDates] = useState<AvailableDate[]>([]);
  const [datesLoading, setDatesLoading] = useState(false);

  const group = addressGroups.find(g => g.addressId === addressId);

  useEffect(() => {
    if (open && addressId) {
      setNewDate(group?.deliveryDate || "");
      setError(null);
      // Fetch available dates
      setDatesLoading(true);
      call<any>("/recharge/delivery-dates/available", { params: { address_id: addressId } })
        .then(res => {
          if (res.ok && res.data?.dates) {
            setAvailableDates(res.data.dates);
          }
        })
        .finally(() => setDatesLoading(false));
    }
  }, [open, addressId]);

  const handleConfirm = async () => {
    if (!sub || !newDate) return;
    // Validation is handled server-side by Qivora

    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/subscription/change-delivery-date", {
        method: "POST",
        body: { subscriptionId: sub.id, deliveryDate: newDate },
      });

      if (!res.ok) throw new Error(res.error || "Failed to change delivery date");

      onSuccess("Delivery date updated for all subscriptions at this address!");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to change delivery date");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800 }}>Change Delivery Date</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {addressId && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This will update the delivery date for <strong>all {group?.allSubs.length || 0} subscriptions</strong> at this address.
            </Alert>

            <Box sx={{ mb: 3, p: 2, bgcolor: COLORS.cream, borderRadius: "8px" }}>
              <Typography sx={{ fontSize: 12, color: COLORS.textMuted, mb: 0.5 }}>Current Delivery Date</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{formatDate(group?.deliveryDate)}</Typography>
            </Box>

            {datesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={24} /></Box>
            ) : availableDates.length > 0 ? (
              <>
                <Typography sx={{ mb: 2, fontSize: 14, color: COLORS.textMuted }}>Select an available delivery date:</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {availableDates.map(d => (
                    <Chip
                      key={d.date}
                      label={`${d.day} ${formatDate(d.date)}`}
                      clickable
                      onClick={() => setNewDate(d.date)}
                      sx={{
                        bgcolor: newDate === d.date ? COLORS.blueLight : QUIZ.color.cream2,
                        border: newDate === d.date ? `2px solid ${COLORS.blue}` : "1px solid #ddd",
                        fontWeight: newDate === d.date ? 700 : 400,
                      }}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <>
                <Typography sx={{ mb: 2, fontSize: 14, color: COLORS.textMuted }}>
                  Select a new delivery date:
                </Typography>
                <TextField fullWidth type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
              </>
            )}

            {newDate && (
              <Box sx={{ p: 2, bgcolor: COLORS.blueLight, borderRadius: "8px" }}>
                <Typography sx={{ fontSize: 12, color: COLORS.blue, fontWeight: 600, mb: 1 }}>New Delivery Date:</Typography>
                <Typography sx={{ fontSize: 13 }}><strong>Deliver:</strong> {formatDate(newDate)}</Typography>
                <Typography sx={{ fontSize: 11, color: COLORS.textMuted, mt: 0.5 }}>Ship and charge dates will be computed automatically based on your delivery rules.</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || !newDate}
          sx={{ bgcolor: COLORS.blue, "&:hover": { bgcolor: COLORS.blueDark }, textTransform: "none" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Update Delivery Date"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
